import { useVirtualizer } from '@tanstack/react-virtual';
import { memo, useRef } from 'react';

import { cn } from '@air/utils-tailwind';

import { WorkspaceAgentActionsMenuListRowSkeletonList } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/actions-menu/WorkspaceAgentActionsMenuListRowSkeletonList';
import { WorkspaceAgentActionsMenuSkillRow } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/actions-menu/WorkspaceAgentActionsMenuSkillRow';
import { type WorkspaceAgentActionsMenuSkillsListProps } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/actions-menu/workspaceAgentActionsMenuPanelSession';
import { WorkspaceAgentSkillsCatalogListFooter } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/skills/WorkspaceAgentSkillsCatalogListFooter';
import {
  estimateWorkspaceAgentActionsMenuSkillRowHeight,
  WORKSPACE_AGENT_ACTIONS_MENU_SKILLS_CATALOG_LOADING_SKELETON_ROW_COUNT,
  WORKSPACE_AGENT_ACTIONS_MENU_SKILLS_FETCH_NEXT_PAGE_SKELETON_ROW_COUNT,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentActionsMenuSkillsLayout';
import { workspaceAgentSkillTriggerMenuEmpty } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentComposerChrome';

const WORKSPACE_AGENT_ACTIONS_MENU_SKILL_OPTION_ID_PREFIX = 'workspace-agent-actions-menu-skill-option-';

export const WorkspaceAgentActionsMenuSkillsList = memo(
  ({
    handleSelectSkill,
    hasSkillsListNextPage,
    isSkillsListFetchNextPageError,
    isSkillsListFetchingNextPage,
    isSkillsListLoading: isLoading,
    requestSkillsListNextPage,
    retrySkillsListNextPage,
    showSkillsEmpty: showEmpty,
    visibleSkills: skills,
  }: WorkspaceAgentActionsMenuSkillsListProps) => {
    const scrollElementRef = useRef<HTMLDivElement | null>(null);

    const virtualizer = useVirtualizer({
      count: skills.length,
      estimateSize: (index) => {
        const skill = skills[index];

        return estimateWorkspaceAgentActionsMenuSkillRowHeight('catalog', Boolean(skill?.description.trim()));
      },
      getScrollElement: () => scrollElementRef.current,
      overscan: 8,
    });

    const virtualItems = virtualizer.getVirtualItems();

    return (
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {isLoading && skills.length === 0 ? (
          <div className="min-h-0 flex-1 overflow-y-auto px-1.5 py-1.5">
            <WorkspaceAgentActionsMenuListRowSkeletonList
              data-testid="WORKSPACE_AGENT_ACTIONS_MENU_SKILLS_STATUS"
              layout="catalog"
              loadingLabel="Loading skills"
              rowCount={WORKSPACE_AGENT_ACTIONS_MENU_SKILLS_CATALOG_LOADING_SKELETON_ROW_COUNT}
            />
          </div>
        ) : null}
        {!isLoading && showEmpty ? (
          <p
            aria-live="polite"
            className={cn('px-2 py-3', workspaceAgentSkillTriggerMenuEmpty())}
            data-testid="WORKSPACE_AGENT_ACTIONS_MENU_SKILLS_STATUS"
            role="status"
          >
            No matching skills
          </p>
        ) : null}
        {skills.length > 0 ? (
          <div
            className="min-h-0 flex-1 overflow-y-auto px-1.5 py-1.5"
            data-testid="WORKSPACE_AGENT_ACTIONS_MENU_SKILLS_LIST"
            ref={scrollElementRef}
          >
            <div className="relative w-full" style={{ height: virtualizer.getTotalSize() }}>
              {virtualItems.map((virtualRow) => {
                const skill = skills[virtualRow.index];

                if (!skill) {
                  return null;
                }

                return (
                  <div
                    className="absolute top-0 left-0 w-full"
                    data-index={virtualRow.index}
                    key={skill.id}
                    ref={virtualizer.measureElement}
                    style={{ transform: `translateY(${virtualRow.start}px)` }}
                  >
                    <WorkspaceAgentActionsMenuSkillRow
                      id={`${WORKSPACE_AGENT_ACTIONS_MENU_SKILL_OPTION_ID_PREFIX}${virtualRow.index}`}
                      layout="catalog"
                      onSelect={handleSelectSkill}
                      skill={skill}
                    />
                  </div>
                );
              })}
            </div>
            <WorkspaceAgentSkillsCatalogListFooter
              hasNextPage={hasSkillsListNextPage}
              includeLoadingMoreStatus={false}
              includePaginationErrorStatus
              includeStatusUi={false}
              isFetchNextPageError={isSkillsListFetchNextPageError}
              isFetchingNextPage={isSkillsListFetchingNextPage}
              listRef={scrollElementRef}
              onRequestNextPage={requestSkillsListNextPage}
              onRetryNextPage={retrySkillsListNextPage}
            />
            {isSkillsListFetchingNextPage ? (
              <WorkspaceAgentActionsMenuListRowSkeletonList
                data-testid="WORKSPACE_AGENT_ACTIONS_MENU_SKILLS_FETCH_NEXT_PAGE_STATUS"
                layout="catalog"
                loadingLabel="Loading more skills"
                rowCount={WORKSPACE_AGENT_ACTIONS_MENU_SKILLS_FETCH_NEXT_PAGE_SKELETON_ROW_COUNT}
              />
            ) : null}
          </div>
        ) : null}
      </div>
    );
  },
);

WorkspaceAgentActionsMenuSkillsList.displayName = 'WorkspaceAgentActionsMenuSkillsList';
